// WP-6.4 (M29 AD-4, LOCKED §2.34, S-32): "Can I afford this?" — an on-demand check triggered by
// the owner submitting an amount+purpose in the AffordabilityBox form, not a background-
// refreshing query. Modeled as a `useMutation` wrapping the GET (no precedent for a GET-as-
// mutation exists yet in lib/goals.ts or lib/reports.ts — every existing GET there is a
// useQuery), which is still the right shape for "fetch triggered by an explicit user action with
// pending/error/success state" rather than cache-driven background data.
import type { CurrencyCode } from '@pfos/engine';
import { affordabilityResponseSchema, type AffordabilityResponse } from '@pfos/shared';
import { useMutation, type UseMutationResult } from '@tanstack/react-query';
import { api } from './api';

export interface AffordabilityCheckInput {
  amountMinor: number;
  currency: CurrencyCode;
  purpose: string;
  /** Defaults server-side to now when omitted (see routes/affordability.ts). */
  asOf?: string;
}

export function useCheckAffordability(): UseMutationResult<
  AffordabilityResponse,
  Error,
  AffordabilityCheckInput
> {
  return useMutation({
    mutationFn: async (input) => {
      const params = new URLSearchParams({
        currency: input.currency,
        amountMinor: String(input.amountMinor),
        purpose: input.purpose,
      });
      if (input.asOf !== undefined) params.set('asOf', input.asOf);
      return affordabilityResponseSchema.parse(
        await api.get(`/api/v1/affordability?${params.toString()}`),
      );
    },
  });
}
